import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    Search,
    Filter,
    Check,
    X,
    User,
    Clock,
    AlertCircle,
    FileText,
    ChevronDown,
    DollarSign,
    CreditCard,
    Loader, Download
} from "lucide-react";
import {
    getAdminAvanceInboxRequest,
    adminAvanceDecisionRequest,
    getAdminAvanceHistoryRequest,
    exportAvanceRequestsExcelRequest,
} from "../../../../redux/actions/avanceRequestActions";
import "../TraitementStyles/TraitementAvances.css";

const TraitementAvances = () => {
    const dispatch = useDispatch();

    // Redux state
    const { adminInbox, loading } = useSelector((state) => state.avanceRequests);

    const [activeTab, setActiveTab] = useState("enCours");
    const [searchTerm, setSearchTerm] = useState("");
    const [showFilters, setShowFilters] = useState(false);

    const [showRejectModal, setShowRejectModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [selectedDemande, setSelectedDemande] = useState(null);
    const [motifRejet, setMotifRejet] = useState("");

    const [filters, setFilters] = useState({
        typeDemande: "",
        departement: "",
        montantMin: "",
        montantMax: "",
        dateDebut: "",
        dateFin: "",
    });

    // Charger les données au montage
    useEffect(() => {
        dispatch(getAdminAvanceInboxRequest());
        dispatch(getAdminAvanceHistoryRequest());
    }, [dispatch]);

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        const d = new Date(dateStr);
        return d.toLocaleDateString("fr-FR", {
            day: "2-digit",
            month: "short",
            year: "numeric"
        });
    };

    const formatMontant = (montant) => {
        if (!montant) return "0";
        return new Intl.NumberFormat("fr-MA", {
            style: "decimal",
            minimumFractionDigits: 0
        }).format(montant);
    };

    const percentSalaire = (demande) => {
        // On suppose que le salaire est dans employeeSalary ou on peut l'estimer
        const salaire = demande.employeeSalary || 10000; // Valeur par défaut
        if (!salaire) return 0;
        return (demande.montantMensuel / salaire) * 100;
    };

    const getStatutBadge = (statut) => {
        const config = {
            PENDING_ADMIN: { label: "En attente", bg: "#f9fefd", color: "#455584" },
            APPROVED: { label: "Approuvé", bg: "#d4edda", color: "#155724" },
            REJECTED: { label: "Rejeté", bg: "#f8d7da", color: "#721c24" },
        };
        const c = config[statut] || config.PENDING_ADMIN;
        return (
            <span className="tavBadge" style={{ backgroundColor: c.bg, color: c.color }}>
                {c.label}
            </span>
        );
    };

    const handleApprove = (demandeId) => {
        dispatch(adminAvanceDecisionRequest({
            id: demandeId,
            decision: true,
            comment: ""
        }));
    };

    const handleReject = (demande) => {
        setSelectedDemande(demande);
        setShowRejectModal(true);
    };

    const confirmReject = () => {
        if (!motifRejet.trim() || !selectedDemande) return;

        dispatch(adminAvanceDecisionRequest({
            id: selectedDemande.id,
            decision: false,
            comment: motifRejet
        }));

        closeReject();
    };

    const showDetails = (demande) => {
        setSelectedDemande(demande);
        setShowDetailsModal(true);
    };

    const closeDetails = () => {
        setShowDetailsModal(false);
        setSelectedDemande(null);
    };

    const closeReject = () => {
        setShowRejectModal(false);
        setMotifRejet("");
        setSelectedDemande(null);
    };

    // Mapper les statuts Redux vers les onglets de l'interface
    const mapStatutToTab = (statut) => {
        if (statut === "PENDING_ADMIN") return "enCours";
        if (statut === "APPROVED") return "approuve";
        if (statut === "REJECTED") return "rejete";
        return "enCours";
    };

    const getFilteredDemandes = () => {
        if (!adminInbox || adminInbox.length === 0) return [];

        let filtered = adminInbox.filter((d) => mapStatutToTab(d.status) === activeTab);

        if (searchTerm) {
            const s = searchTerm.toLowerCase();
            filtered = filtered.filter(
                (d) =>
                    (d.employeeName && d.employeeName.toLowerCase().includes(s)) ||
                    (d.employeeMatricule && d.employeeMatricule.toLowerCase().includes(s)) ||
                    (d.avanceType && d.avanceType.toLowerCase().includes(s))
            );
        }

        if (filters.typeDemande) {
            filtered = filtered.filter((d) => d.avanceType === filters.typeDemande);
        }

        if (filters.montantMin) {
            filtered = filtered.filter((d) => d.montantDemande >= Number(filters.montantMin));
        }

        if (filters.montantMax) {
            filtered = filtered.filter((d) => d.montantDemande <= Number(filters.montantMax));
        }

        if (filters.dateDebut) {
            filtered = filtered.filter((d) => {
                const dateCreation = new Date(d.createdAt).toISOString().split('T')[0];
                return dateCreation >= filters.dateDebut;
            });
        }

        if (filters.dateFin) {
            filtered = filtered.filter((d) => {
                const dateCreation = new Date(d.createdAt).toISOString().split('T')[0];
                return dateCreation <= filters.dateFin;
            });
        }

        return filtered;
    };

    const tabs = useMemo(() => {
        if (!adminInbox) return [
            { id: "enCours", label: "En cours", count: 0 },
            { id: "approuve", label: "Approuvées", count: 0 },
            { id: "rejete", label: "Rejetées", count: 0 },
        ];

        return [
            {
                id: "enCours",
                label: "En cours",
                count: adminInbox.filter((d) => d.status === "PENDING_ADMIN").length
            },
            {
                id: "approuve",
                label: "Approuvées",
                count: adminInbox.filter((d) => d.status === "APPROVED").length
            },
            {
                id: "rejete",
                label: "Rejetées",
                count: adminInbox.filter((d) => d.status === "REJECTED").length
            },
        ];
    }, [adminInbox]);

    const typesDemandes = useMemo(() => {
        if (!adminInbox) return [];
        return [...new Set(adminInbox.map((d) => d.avanceType).filter(Boolean))];
    }, [adminInbox]);

    const filtered = getFilteredDemandes();

    if (loading && (!adminInbox || adminInbox.length === 0)) {
        return (
            <div className="tavPage">
                <div className="tavWrap" style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                    <Loader size={32} className="spin" />
                </div>
            </div>
        );
    }

    return (
        <div className="tavPage">
            <div className="tavWrap">
                {/* Header */}
                <div className="tavHeader">
                    <div>
                        <h1>Traitement des demandes d'avances et prêts</h1>
                        <p>Gérez et validez les demandes d'avances sur salaire et prêts de vos collaborateurs</p>
                    </div>
                </div>

                {/* Search + Filters */}
                <div className="tavCard">
                    <div className="tavControls">
                        <div className="tavSearch">
                            <Search size={18}/>
                            <input
                                type="text"
                                placeholder="Rechercher par matricule, nom, type..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>

                        <button type="button" className="tavFilterBtn" onClick={() => setShowFilters((v) => !v)}>
                            <Filter size={16}/>
                            Filtres
                            <ChevronDown size={16} className={`tavChevron ${showFilters ? "is-open" : ""}`}/>
                        </button>
                        <button
                            type="button"
                            className="tavFilterBtn"
                            style={{marginLeft: "auto", background: "#10b981", color: "white", border: "none"}}
                            onClick={() => dispatch(exportAvanceRequestsExcelRequest("admin_inbox"))}
                        >
                            <Download size={16}/>
                            Exporter Excel
                        </button>
                    </div>

                    {showFilters && (
                        <div className="tavFilters">
                            <div className="tavField">
                                <label>Type de demande</label>
                                <select value={filters.typeDemande} onChange={(e) => setFilters((p) => ({ ...p, typeDemande: e.target.value }))}>
                                    <option value="">Tous</option>
                                    {typesDemandes.map((t) => (
                                        <option key={t} value={t}>
                                            {t}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="tavField">
                                <label>Montant min (dhs)</label>
                                <input
                                    type="number"
                                    value={filters.montantMin}
                                    onChange={(e) => setFilters((p) => ({ ...p, montantMin: e.target.value }))}
                                    placeholder="0"
                                />
                            </div>

                            <div className="tavField">
                                <label>Montant max (dhs)</label>
                                <input
                                    type="number"
                                    value={filters.montantMax}
                                    onChange={(e) => setFilters((p) => ({ ...p, montantMax: e.target.value }))}
                                    placeholder="∞"
                                />
                            </div>

                            <div className="tavField">
                                <label>Date début</label>
                                <input
                                    type="date"
                                    value={filters.dateDebut}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateDebut: e.target.value }))}
                                />
                            </div>

                            <div className="tavField">
                                <label>Date de fin</label>
                                <input
                                    type="date"
                                    value={filters.dateFin}
                                    onChange={(e) => setFilters((p) => ({ ...p, dateFin: e.target.value }))}
                                />
                            </div>
                        </div>
                    )}
                </div>

                {/* Tabs */}
                <div className="tavCard tavCard--noPad">
                    <div className="tavTabs">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                type="button"
                                onClick={() => setActiveTab(tab.id)}
                                className={`tavTab ${activeTab === tab.id ? "is-active" : ""}`}
                            >
                                {tab.label}
                                <span className={`tavCount ${activeTab === tab.id ? "is-active" : ""}`}>
                                    {tab.count}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Table */}
                    <div className="tavTableWrap">
                        {filtered.length === 0 ? (
                            <div className="tavEmpty">
                                <AlertCircle size={46} />
                                <div>Aucune demande trouvée</div>
                            </div>
                        ) : (
                            <table className="tavTable">
                                <thead>
                                <tr>
                                    <th>Matricule</th>
                                    <th>Collaborateur</th>
                                    <th>Type de demande</th>
                                    <th>Montant demandé</th>
                                    <th>Remboursement</th>
                                    <th>Date de la demande</th>
                                    <th>Statut</th>
                                    <th className="is-right">Actions</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filtered.map((demande) => (
                                    <tr
                                        key={demande.id}
                                        className={demande.urgent ? "tavRowUrgent" : ""}
                                        onClick={() => showDetails(demande)}
                                        title={demande.urgent ? "Demande urgente" : ""}
                                    >
                                        <td>
                                            <span className="tavPrimary">{demande.employeeMatricule || "N/A"}</span>
                                        </td>

                                        <td>
                                            <div className="tavName">
                                                <span className="tavPrimary">{demande.employeeName}</span>
                                                {demande.urgent && <span className="tavFire">🔥</span>}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavType">
                                                <span className="tavDot" style={{ backgroundColor: "#2f478f" }} />
                                                <div>
                                                    <div className="tavPrimary">{demande.avanceType}</div>
                                                </div>
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavAmount">
                                                <div className="tavAmountMain">{formatMontant(demande.montantDemande)} dhs</div>
                                                {demande.tauxInteret > 0 && (
                                                    <div className="tavSub">Taux: {demande.tauxInteret}%</div>
                                                )}
                                            </div>
                                        </td>

                                        <td>
                                            <div className="tavPrimary">{demande.nombreMensualites} mois</div>
                                            <div className="tavSub">{formatMontant(demande.montantMensuel)} dhs/mois</div>
                                        </td>

                                        <td>
                                            <span className="tavMuted">{formatDate(demande.createdAt)}</span>
                                        </td>

                                        <td>{getStatutBadge(demande.status)}</td>

                                        <td className="is-right" onClick={(e) => e.stopPropagation()}>
                                            {activeTab === "enCours" ? (
                                                <div className="tavRowActions">
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--ok"
                                                        onClick={() => handleApprove(demande.id)}
                                                        title="Approuver"
                                                        disabled={loading}
                                                    >
                                                        <Check size={16} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="tavActionBtn tavActionBtn--no"
                                                        onClick={() => handleReject(demande)}
                                                        title="Refuser"
                                                        disabled={loading}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="tavDetailsBtn"
                                                    onClick={() => showDetails(demande)}
                                                >
                                                    Détails
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        )}
                    </div>
                </div>
            </div>

            {/* DETAILS MODAL */}
            {showDetailsModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon">
                                    <CreditCard size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Détails de la demande #{selectedDemande.id}</div>
                                    <div className="tavModal__sub">Demande du {formatDate(selectedDemande.createdAt)}</div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeDetails} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavGrid2">
                                {/* Infos collaborateur */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <User size={16} /> Informations collaborateur
                                    </div>

                                    <div className="tavKv">
                                        <span>Matricule</span>
                                        <b>{selectedDemande.employeeMatricule || "N/A"}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Nom</span>
                                        <b>{selectedDemande.employeeName}</b>
                                    </div>
                                </div>

                                {/* Détails demande */}
                                <div className="tavPanel">
                                    <div className="tavPanel__title">
                                        <CreditCard size={16} /> Détails de la demande
                                    </div>

                                    <div className="tavKv">
                                        <span>Type</span>
                                        <b>{selectedDemande.avanceType}</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Montant demandé</span>
                                        <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualités</span>
                                        <b>{selectedDemande.nombreMensualites} mois</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Taux d'intérêt</span>
                                        <b>{selectedDemande.tauxInteret || 0}%</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Mensualité</span>
                                        <b>{formatMontant(selectedDemande.montantMensuel)} dhs</b>
                                    </div>
                                    <div className="tavKv">
                                        <span>Total à rembourser</span>
                                        <b>{formatMontant(selectedDemande.montantTotal)} dhs</b>
                                    </div>
                                </div>

                                {/* Motif */}
                                <div className="tavPanel tavPanel--full">
                                    <div className="tavPanel__title">
                                        <FileText size={16} /> Motif de la demande
                                    </div>
                                    <div className="tavText">{selectedDemande.motif || " - "}</div>
                                </div>

                                {/* Justificatifs */}
                                {selectedDemande.justificatifs && selectedDemande.justificatifs.length > 0 && (
                                    <div className="tavPanel">
                                        <div className="tavPanel__title">
                                            <FileText size={16} /> Justificatifs fournis
                                        </div>
                                        <div className="tavTags">
                                            {selectedDemande.justificatifs.map((doc, idx) => (
                                                <span key={idx} className="tavTag">
                                                    {doc.split('/').pop()}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Décision manager (si applicable) */}
                                {selectedDemande.managerDecision !== null && (
                                    <div className="tavPanel tavPanel--full">
                                        <div className="tavPanel__title">
                                            <Check size={16} /> Validation Manager
                                        </div>
                                        <div className="tavKv">
                                            <span>Décision</span>
                                            <b style={{ color: selectedDemande.managerDecision ? "#10b981" : "#ef4444" }}>
                                                {selectedDemande.managerDecision ? "Approuvé" : "Rejeté"}
                                            </b>
                                        </div>
                                        {selectedDemande.managerName && (
                                            <div className="tavKv">
                                                <span>Par</span>
                                                <b>{selectedDemande.managerName}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerDecisionAt && (
                                            <div className="tavKv">
                                                <span>Date</span>
                                                <b>{formatDate(selectedDemande.managerDecisionAt)}</b>
                                            </div>
                                        )}
                                        {selectedDemande.managerComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">Commentaire</div>
                                                <div className="tavRejectText">{selectedDemande.managerComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                {/* Validation RH (si déjà traitée) */}
                                {selectedDemande.status !== "PENDING_ADMIN" && selectedDemande.adminDecision !== null && (
                                    <div className={`tavPanel tavPanel--full ${selectedDemande.status === "APPROVED" ? "tavValidOk" : "tavValidNo"}`}>
                                        <div className="tavPanel__title">
                                            {selectedDemande.status === "APPROVED" ? <Check size={16} /> : <X size={16} />}
                                            {selectedDemande.status === "APPROVED" ? " Demande approuvée" : " Demande rejetée"}
                                        </div>

                                        <div className="tavKv">
                                            <span>Date de validation</span>
                                            <b>{formatDate(selectedDemande.adminDecisionAt)}</b>
                                        </div>
                                        {selectedDemande.adminName && (
                                            <div className="tavKv">
                                                <span>Validé par</span>
                                                <b>{selectedDemande.adminName}</b>
                                            </div>
                                        )}

                                        {selectedDemande.adminComment && (
                                            <div className="tavRejectBox">
                                                <div className="tavRejectLabel">
                                                    {selectedDemande.status === "APPROVED" ? "Commentaire" : "Motif du rejet"}
                                                </div>
                                                <div className="tavRejectText">{selectedDemande.adminComment}</div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Actions footer */}
                        <div className="tavModal__footer">
                            {selectedDemande.status === "PENDING_ADMIN" ? (
                                <>
                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--danger"
                                        onClick={() => {
                                            closeDetails();
                                            handleReject(selectedDemande);
                                        }}
                                        disabled={loading}
                                    >
                                        Refuser
                                    </button>

                                    <button
                                        type="button"
                                        className="tavBtn tavBtn--ok"
                                        onClick={() => {
                                            handleApprove(selectedDemande.id);
                                            closeDetails();
                                        }}
                                        disabled={loading}
                                    >
                                        {loading ? <Loader size={16} className="spin" /> : "Approuver"}
                                    </button>

                                    <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                        Fermer
                                    </button>
                                </>
                            ) : (
                                <button type="button" className="tavBtn tavBtn--ghost" onClick={closeDetails}>
                                    Fermer
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* REJECT MODAL */}
            {showRejectModal && selectedDemande && (
                <div className="tavModal">
                    <div className="tavModal__panel tavModal__panel--small" role="dialog" aria-modal="true">
                        <div className="tavModal__header">
                            <div className="tavModal__headLeft">
                                <div className="tavModal__icon tavModal__icon--danger">
                                    <AlertCircle size={18} />
                                </div>
                                <div>
                                    <div className="tavModal__title">Refuser la demande d'avance/prêt</div>
                                    <div className="tavModal__sub">
                                        {selectedDemande.employeeName} — {selectedDemande.avanceType}
                                    </div>
                                </div>
                            </div>

                            <button type="button" className="tavIconBtn" onClick={closeReject} aria-label="Fermer">
                                <X size={18} />
                            </button>
                        </div>

                        <div className="tavModal__body">
                            <div className="tavMiniCard">
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Montant demandé</span>
                                    <b className="tavAccent">{formatMontant(selectedDemande.montantDemande)} dhs</b>
                                </div>
                                <div className="tavMiniRow">
                                    <span className="tavMuted">Remboursement</span>
                                    <b>
                                        {selectedDemande.nombreMensualites} mois × {formatMontant(selectedDemande.montantMensuel)} dhs
                                    </b>
                                </div>
                            </div>

                            <label className="tavLabel">
                                Motif du rejet <span className="tavReq">*</span>
                            </label>
                            <textarea
                                value={motifRejet}
                                onChange={(e) => setMotifRejet(e.target.value)}
                                rows={5}
                                placeholder="Veuillez préciser le motif du rejet de cette demande..."
                                className="tavTextarea"
                            />
                            <div className="tavHelp">Ce motif sera communiqué au collaborateur</div>
                        </div>

                        <div className="tavModal__footer">
                            <button type="button" className="tavBtn tavBtn--ghost" onClick={closeReject}>
                                Annuler
                            </button>

                            <button
                                type="button"
                                className="tavBtn tavBtn--danger"
                                onClick={confirmReject}
                                disabled={!motifRejet.trim() || loading}
                            >
                                {loading ? <Loader size={16} className="spin" /> : "Confirmer le rejet"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TraitementAvances;
